import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams, useSearch } from '@tanstack/react-router';
import {
  directory,
  isForbidden,
  mileage,
  programs,
  schedule,
  services,
  timekeeping,
  timesheets,
  users,
} from '@cwos/api-client';
import {
  Avatar,
  ErrorPanel,
  Pill,
  Refused,
  ScopeNote,
  SkeletonSet,
  SuccessEmpty,
  useTabListKeys,
} from '@cwos/ui-admin';
import {
  mayAssignWorkerAuthority,
  mayEditWorkerRecord,
  mayListWorkers,
  mayReadIntegrationsHealth,
  usePermissions,
} from '../permissions.js';
import { authorityGate, gateOpen, type AuthorityGate } from '../authorityGate.js';
import { GateNotice } from '../GateNotice.js';
import { ListState } from '../ListStates.js';
import { WorkerAvailability } from './WorkerAvailability.js';
import { listPhase, nameResolver } from '../listState.js';
import { pickerState } from '../pickerState.js';
import { useNav } from '../navigation.js';
import { toLocalDateString } from '../workforce/workforceLogic.js';
import { SettingsField, SettingsHeader } from '../settings/common.js';
import {
  readSupervisorId,
  SUPERVISOR_UNREAD,
  workerEditDraftFrom,
  workerEditIsEmpty,
  workerEditProblem,
  workerPatchAssignsAuthority,
  workerUpdatePayload,
  type WorkerEditDraft,
} from './directoryLogic.js';
import {
  formatClock,
  formatDay,
  formatDuration,
  formatHours,
  timesheetStatus,
} from '../time/timeLogic.js';
import { LoadFailure } from '../LoadFailure.js';
import { FindingsPanel } from '../insights/FindingsPanel.js';
import {
  CALENDAR_STATE_TONE,
  boardDays,
  boardWindow,
  calendarStateLabel,
  visitState,
} from '../schedule/scheduleBoard.js';
import { clientNameResolver } from '../visits/visitLogic.js';
import { useConnectorLookup } from '../visits/provenance.js';
import { OWN_CASELOAD } from '../delivery.js';
import { useOrgCalendar } from '../schedule/orgCalendar.js';

/**
 * The worker profile — one person, across the operational spine.
 *
 * Team was a directory: a name, a role, a status. The question an administrator
 * actually asks is "what is going on with this person" — their week, their
 * schedule, what they recorded, what they drove, and what is unresolved. Those
 * facts lived in five APIs that no screen joined.
 *
 * Each tab is a real query against a real route. Where a capability does not
 * exist in the backend — supervision requirements, credentials — the tab is
 * ABSENT rather than present-and-empty, because an empty tab reads as "this
 * worker has none" when the truth is "Phibian does not model this yet".
 */

const TABS = [
  { key: 'overview', label: 'Overview' },
  { key: 'time', label: 'Time' },
  { key: 'schedule', label: 'Schedule' },
  { key: 'mileage', label: 'Mileage' },
  { key: 'clients', label: 'Clients' },
] as const;

type TabKey = (typeof TABS)[number]['key'];

const isTab = (v: unknown): v is TabKey => TABS.some((t) => t.key === v);

export function WorkerProfile() {
  const navigate = useNav();
  const permissions = usePermissions();
  const { has } = permissions;
  const params = useParams({ strict: false }) as { workerId?: string };
  const workerId = params.workerId ?? '';
  /**
   * `?tab=schedule` deep-links a section (Staff analytics rows, the calendar's
   * row heads); a Staff row adds its period as `from`/`to`.
   */
  const search = useSearch({ strict: false }) as { tab?: unknown; from?: unknown; to?: unknown };
  const period = useMemo(() => schedulePeriod(search.from, search.to), [search.from, search.to]);
  const [tab, setTab] = useState<TabKey>(() => (isTab(search.tab) ? search.tab : 'overview'));
  useEffect(() => {
    if (isTab(search.tab)) setTab(search.tab);
  }, [search.tab]);
  // Above the early returns below — hooks cannot sit behind a conditional.
  const sectionTabs = useTabListKeys<HTMLDivElement>();

  const worker = useQuery({
    queryKey: ['directory', 'worker', workerId],
    queryFn: () => directory.worker(workerId),
    enabled: workerId !== '' && has('directory:read'),
  });

  // Unknown is not denied. This branch used to fire off `has()` alone, which is
  // `false` while `/auth/me` is in flight and `false` after it failed — so the
  // screen refused AND (through the `enabled:` above) fetched nothing, on a
  // permission the reader may well hold. Only a resolved set may refuse.
  const readGate = authorityGate(permissions, 'directory:read');
  if (!gateOpen(readGate)) {
    return (
      <GateNotice
        gate={readGate}
        what="view worker records"
        heading="You don’t have access to worker records"
        detail="Viewing people in the organization needs the directory permission."
        onRetry={permissions.refetch}
      />
    );
  }

  if (worker.isLoading) return <SkeletonSet surface="drawer" />;
  // A 403 here is a PERMISSION fact and must not be dressed as a transient
  // failure with a Retry button that can never succeed. `GET
  // /directory/workers/:id` answers for your own record and for a worker whose
  // `worker.supervisor_id` is you; anybody else needs `user:read`, which
  // 1755046800000_seed-permissions grants to org_admin and supervisor and
  // deliberately not to worker. Distinguishing the two is the same rule
  // `nameResolver` applies one row at a time.
  if (worker.isError && !worker.data && isForbidden(worker.error)) {
    return (
      <section className="rn-card">
        <h2 className="rn-card-title">You don’t have access to this worker’s record</h2>
        <p className="rn-muted">
          You can open your own record and the records of the workers you supervise. Anyone else in
          the organization needs the “View users in the organization” permission.
        </p>
      </section>
    );
  }
  if (worker.isError && !worker.data) {
    return (
      <LoadFailure
        error={worker.error}
        mainRead
        message="We couldn’t load this worker just now."
        onRetry={() => void worker.refetch()}
      />
    );
  }
  const w = worker.data;
  if (!w) return null;

  return (
    <div className="rn-col" style={{ maxWidth: 1080 }}>
      <button type="button" className="rn-btn-quiet tc-back" onClick={() => navigate('/people')}>
        ← Team
      </button>

      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-12)' }}>
        <Avatar
          name={w.displayName}
          size={44}
          src={w.userId && w.photoUpdatedAt ? users.photoUrl(w.userId, w.photoUpdatedAt) : null}
        />
        <SettingsHeader
          title={w.displayName}
          blurb={
            [w.roleTitle, w.employmentStatus === 'active' ? null : w.employmentStatus]
              .filter(Boolean)
              .join(' · ') || undefined
          }
        />
      </div>

      {/* `aria-pressed` dropped (invalid on `role="tab"`), ids and a panel
          added, arrow keys supplied by the hook. */}
      <div className="rn-tabs" role="tablist" aria-label="Worker sections" {...sectionTabs}>
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            role="tab"
            id={`worker-tab-${t.key}`}
            aria-controls="worker-panel"
            className="rn-tab"
            aria-selected={tab === t.key}
            onClick={() => setTab(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div id="worker-panel" role="tabpanel" aria-labelledby={`worker-tab-${tab}`}>
        {tab === 'overview' ? <Overview workerId={workerId} worker={w} /> : null}
        {tab === 'time' ? <TimeTab workerId={workerId} /> : null}
        {tab === 'schedule' ? <ScheduleTab workerId={workerId} period={period} /> : null}
        {tab === 'mileage' ? <MileageTab workerId={workerId} /> : null}
        {tab === 'clients' ? <ClientsTab workerId={workerId} /> : null}
      </div>
    </div>
  );
}

// ------------------------------------------------------------------ overview

function Overview({ workerId, worker }: { workerId: string; worker: directory.Worker }) {
  const navigate = useNav();
  const permissions = usePermissions();
  const { has } = permissions;
  const [editing, setEditing] = useState(false);

  /**
   * The roster, for ONE name: whoever this worker reports to.
   *
   * `mayListWorkers` gates it because `GET /directory/workers` REFUSES a reader
   * without `user:read` rather than narrowing, and `nameResolver` turns the
   * three ways a name can be missing — withheld, pending, failed — into three
   * different sentences instead of one claim about permission.
   */
  const roster = useQuery({
    queryKey: ['directory', 'workers'],
    queryFn: () => directory.workers(),
    enabled: mayListWorkers(permissions),
  });
  const nameOf = nameResolver(
    { data: roster.data, isLoading: roster.isLoading, isError: roster.isError },
    mayListWorkers(permissions),
  );

  const week = useQuery({
    queryKey: ['timesheets', 'week', workerId, 'current'],
    queryFn: () => timesheets.workerWeek(workerId),
    enabled: has('timesheet:read'),
  });
  const current = useQuery({
    queryKey: ['timekeeping', 'current', workerId],
    queryFn: () => timekeeping.currentSession(workerId),
    enabled: has('time:read'),
    refetchInterval: 60_000,
  });
  const sched = useQuery({
    queryKey: ['schedule', 'week', workerId],
    queryFn: () => schedule.workerWeek(workerId),
    enabled: has('schedule:read'),
  });

  // CORE-AUD-216: an organization that has never clocked anyone has no
  // recorded hours to show — "Not measured", never a measured 0.00h.
  const notMeasured = week.data?.hasTimeClock === false;
  const recorded = notMeasured ? null : (week.data?.aggregate.totalMinutes ?? null);
  const expected = worker.expectedWeeklyHours;
  // A "below expected hours" claim needs both numbers. With either missing the
  // card says so rather than implying a shortfall from a null.
  const shortfall = recorded !== null && expected !== null ? expected - recorded / 60 : null;

  return (
    <div className="rn-col">
      <section className="rn-card">
        <h2 className="rn-card-title">This week</h2>
        <div className="tc-kpis" style={{ marginTop: 'var(--space-12)' }}>
          <div className="db-kpi">
            <span className="db-kpi-label">Recorded</span>
            <span className="db-kpi-value">
              {week.isError
                ? '—'
                : week.isLoading
                  ? '…'
                  : notMeasured
                    ? 'Not measured'
                    : formatHours(recorded)}
            </span>
            <span className="db-kpi-foot">
              <span className="rn-muted">
                {notMeasured
                  ? 'No time clock connected — hours are not recorded here'
                  : expected !== null
                    ? `Expected ${expected}h`
                    : 'No expected hours set'}
              </span>
            </span>
          </div>
          <div className="db-kpi">
            <span className="db-kpi-label">Right now</span>
            <span className="db-kpi-value" style={{ fontSize: 22 }}>
              {/* "Not clocked in" is a fact about a person's day. It was being
                  printed from a failed request, where the honest answer is that
                  we do not know. */}
              {current.isError
                ? 'Unknown'
                : current.isLoading
                  ? '…'
                  : current.data
                    ? 'Clocked in'
                    : 'Not clocked in'}
            </span>
            <span className="db-kpi-foot">
              <span className="rn-muted">
                {current.isError
                  ? 'We couldn’t reach the clock'
                  : current.data
                    ? `Since ${formatClock(current.data.clockedInAt)}`
                    : 'No open session'}
              </span>
            </span>
          </div>
          <div className="db-kpi">
            <span className="db-kpi-label">Shifts this week</span>
            <span className="db-kpi-value">{sched.data?.summary.total ?? '—'}</span>
            <span className="db-kpi-foot">
              {/* This said "Loading" forever after a failed schedule read, which
                  is the quietest possible lie: nothing is loading, and nobody is
                  coming back to it. */}
              <span className="rn-muted">
                {sched.data
                  ? `${sched.data.summary.withoutSession} without a session`
                  : !has('schedule:read')
                    ? 'Needs the schedule permission'
                    : sched.isError
                      ? 'Couldn’t be read'
                      : 'Loading'}
              </span>
            </span>
          </div>
        </div>

        {shortfall !== null && shortfall > 0.5 ? (
          <p className="rn-muted" style={{ marginTop: 'var(--space-12)' }}>
            {shortfall.toFixed(1)} hours below the expected week so far. This counts recorded time
            only — approved leave is not modelled in Phibian, so a shortfall is not by itself a
            problem.
          </p>
        ) : null}
      </section>

      {/* WHAT PHIBIAN NOTICED ABOUT THIS PERSON, beside the week it is about.
          This is a MANAGER reading a worker's record — the surface
          OPERATIONAL-LIBRARY-V1.md §2.1 calls management. The worker's own
          view (`worker-web/WorkerWeb.tsx`) does not carry this panel and must
          not: a worker meets a finding as the task it produced. */}
      <FindingsPanel scope={{ kind: 'worker', workerId, who: worker.displayName }} />

      {/* The second half of the owner's path: worker → location history. The
          card is shown only to somebody holding the location-history grant,
          and says what it opens rather than being an unlabelled map icon. */}
      {has('location_history:view') ? (
        <section className="rn-card">
          <div className="rn-card-head">
            <h2 className="rn-card-title">Where they have worked</h2>
            <button
              type="button"
              className="rn-btn-quiet"
              onClick={() =>
                navigate(
                  `/field/map?date=${encodeURIComponent(toLocalDateString(new Date()))}` +
                    `&worker=${encodeURIComponent(workerId)}`,
                )
              }
            >
              Open location history
            </button>
          </div>
          <p className="rn-muted" style={{ marginTop: 'var(--space-6)' }}>
            Clock-in and clock-out positions, the recorded path between them, and the sites and
            geofences for the day you choose. Opening it is recorded against your name.
          </p>
        </section>
      ) : null}

      <section className="rn-card">
        <div className="rn-card-head">
          <h2 className="rn-card-title">Record</h2>
          {/* Absent, not disabled, for a reader who cannot write it — and absent
              while the permission set is unknown, which is what default-deny is
              right for. `PATCH /directory/workers/:id` had no caller anywhere in
              this app until this button; see `directoryLogic.workerUpdatePayload`
              for what that cost an organization. */}
          {mayEditWorkerRecord(permissions) && !editing ? (
            <button type="button" className="rn-btn-quiet" onClick={() => setEditing(true)}>
              Edit record
            </button>
          ) : null}
        </div>
        <dl className="cmp-facts">
          <div>
            <dt>Employment status</dt>
            <dd>{worker.employmentStatus}</dd>
          </div>
          <div>
            <dt>Role</dt>
            <dd>{worker.roleTitle ?? 'Not recorded'}</dd>
          </div>
          <div>
            <dt>Reports to</dt>
            <dd>{supervisorLabel(worker, nameOf)}</dd>
          </div>
          <div>
            <dt>Expected weekly hours</dt>
            <dd>
              {worker.expectedWeeklyHours !== null ? `${worker.expectedWeeklyHours}h` : 'Not set'}
            </dd>
          </div>
          <div>
            <dt>Sign-in account</dt>
            {/* A worker record and a login are deliberately separate things in
                this API — an office administrator may hold no worker row, and a
                worker may hold no login — so "not joined" is a real state and
                not a defect. It IS the reason a person's app shows them nothing,
                which is worth being able to see. */}
            <dd>{worker.userId ? 'Joined to a login' : 'No login joined to this record'}</dd>
          </div>
          <div>
            <dt>Time source</dt>
            <dd>{worker.timeSource}</dd>
          </div>
          <div>
            <dt>Started</dt>
            <dd>{worker.hiredOn ? formatDay(worker.hiredOn) : 'Not recorded'}</dd>
          </div>
          <div>
            <dt>Ended</dt>
            <dd>{worker.endedOn ? formatDay(worker.endedOn) : 'Still employed'}</dd>
          </div>
        </dl>
      </section>

      {editing ? <WorkerEditor worker={worker} onClose={() => setEditing(false)} /> : null}

      {week.data && week.data.exceptions.length > 0 ? (
        <section className="rn-card">
          <div className="rn-card-head">
            <h2 className="rn-card-title">Unresolved this week</h2>
            <button
              type="button"
              className="rn-btn-quiet"
              onClick={() => navigate(`/time/timesheets/${workerId}`)}
            >
              Open the timesheet
            </button>
          </div>
          <ul className="tc-rows">
            {week.data.exceptions.map((ex) => (
              <li key={`${ex.flag}:${ex.refId}`} className="tc-row">
                <button
                  type="button"
                  className="tc-row-btn"
                  onClick={() => navigate(`/sessions/${ex.refId}`)}
                >
                  <span className="tc-row-main">
                    <span className="tc-row-name">{ex.flag.replace(/_/g, ' ')}</span>
                  </span>
                  <span className="tc-row-side">
                    <Pill variant="look" dot>
                      Review
                    </Pill>
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </section>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------- time

function TimeTab({ workerId }: { workerId: string }) {
  const navigate = useNav();
  const permissions = usePermissions();
  const { has } = permissions;
  // Two weeks back: enough to answer "what happened recently" without pulling a
  // worker's entire history into a browser.
  const since = useMemo(() => new Date(Date.now() - 14 * 86_400_000).toISOString(), []);

  const sessions = useQuery({
    queryKey: ['timekeeping', 'sessions', workerId, since],
    queryFn: () => timekeeping.workerSessions(workerId, { since }),
    enabled: has('time:read'),
  });
  const week = useQuery({
    queryKey: ['timesheets', 'week', workerId, 'current'],
    queryFn: () => timesheets.workerWeek(workerId),
    enabled: has('timesheet:read'),
  });

  const timeGate = authorityGate(permissions, 'time:read');
  if (!gateOpen(timeGate)) {
    return (
      <PanelGate
        gate={timeGate}
        what="time records"
        needs="the time permission"
        onRetry={permissions.refetch}
      />
    );
  }

  const rows = sessions.data ?? [];
  const status = week.data ? timesheetStatus(week.data.period.status) : null;

  return (
    <div className="rn-col">
      {week.data ? (
        <section className="rn-card">
          <div className="rn-card-head">
            <h2 className="rn-card-title">This week’s timesheet</h2>
            {status ? (
              <Pill variant={status.tone} dot>
                {status.label}
              </Pill>
            ) : null}
          </div>
          <button
            type="button"
            className="rn-btn-quiet"
            onClick={() => navigate(`/time/timesheets/${workerId}`)}
          >
            {week.data.hasTimeClock === false
              ? 'Open the week — not measured, no time clock connected'
              : `Open the week — ${formatHours(week.data.aggregate.totalMinutes)} recorded`}
          </button>
        </section>
      ) : null}

      <section className="rn-card">
        <h2 className="rn-card-title">Sessions, last 14 days</h2>
        {/* `sessions.data ?? []` meant a failed read told a supervisor this
            person had not worked in a fortnight. */}
        <ListState
          phase={listPhase(sessions, rows.length)}
          rows={5}
          onRetry={() => void sessions.refetch()}
          failedMessage="We couldn’t load this person’s sessions just now. This does not mean there are none."
          refused={<Refused message="Your account cannot read this person’s time records." />}
          empty={
            // CORE-AUD-216: with no time clock, "no sessions" is not a green all-clear.
            week.data?.hasTimeClock === false ? (
              <p className="rn-muted">
                Not measured — this organization has no time clock connected, so no sessions are
                recorded here.
              </p>
            ) : (
              <SuccessEmpty
                heading="No sessions in the last two weeks"
                message="Clock sessions appear here as they are recorded."
              />
            )
          }
        >
          <ul className="tc-rows">
            {rows.map((s) => (
              <li key={s.id} className="tc-row">
                <button
                  type="button"
                  className="tc-row-btn"
                  onClick={() => navigate(`/sessions/${s.id}`)}
                >
                  <span className="tc-row-main">
                    <span className="tc-row-name">{formatDay(s.clockedInAt)}</span>
                    <span className="rn-muted">
                      {formatClock(s.clockedInAt)} →{' '}
                      {s.clockedOutAt ? formatClock(s.clockedOutAt) : 'still open'}
                      {s.autoClosed ? ' · closed automatically' : ''}
                    </span>
                  </span>
                  <span className="tc-row-side">
                    <span className="tc-elapsed">{formatDuration(s.computedMinutes)}</span>
                    {s.clockedOutAt === null ? (
                      <Pill variant="urgent" dot>
                        Open
                      </Pill>
                    ) : null}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </ListState>
      </section>
    </div>
  );
}

// ------------------------------------------------------------------ schedule

/**
 * The worker's schedule: the client visits they are credited with (the same
 * canonical rows and states the calendar draws) and their shifts. Two reads on
 * two keys — either half can be withheld without hiding the other.
 */
function ScheduleTab({ workerId, period }: { workerId: string; period: SchedulePeriod | null }) {
  return (
    <div className="rn-col">
      <WorkerVisits workerId={workerId} period={period} />
      <WorkerShifts workerId={workerId} />
    </div>
  );
}

/** An analytics period handed over in the link: org days, `to` EXCLUSIVE, `from` null = all time. */
interface SchedulePeriod {
  from: string | null;
  to: string;
}

const ISO_DAY = /^\d{4}-\d{2}-\d{2}$/;

/** `?from=&to=` from a Staff analytics row, or null (then the current week). */
function schedulePeriod(from: unknown, to: unknown): SchedulePeriod | null {
  if (typeof to !== 'string' || !ISO_DAY.test(to)) return null;
  if (from === undefined) return { from: null, to };
  if (typeof from !== 'string' || !ISO_DAY.test(from) || from >= to) return null;
  return { from, to };
}

/**
 * THIS WEEK'S VISITS — or the analytics period a Staff row was opened from —
 * credited the canonical way: `?workerId=` matches the visit's worker OR any
 * resolved staff party (SERVICES-API), so a group visit appears here for each
 * person who delivered it.
 */
function WorkerVisits({ workerId, period }: { workerId: string; period: SchedulePeriod | null }) {
  const navigate = useNav();
  const permissions = usePermissions();
  const { has } = permissions;
  const calendar = useOrgCalendar();
  const week = useMemo(() => {
    // The ORGANIZATION's days, as the Staff row counted them (the drill's window).
    if (period) return services.dayWindow(period.from, period.to, calendar.timeZone);
    const w = boardWindow(boardDays(new Date(), 'week'));
    // The roster's window is closed at both ends; the week is half-open.
    return { ...w, to: new Date(Date.parse(w.to) - 1).toISOString() };
  }, [period, calendar.timeZone]);
  const visits = useQuery({
    queryKey: ['services', 'scheduled', 'worker', workerId, week.from, week.to],
    // A period drill counts discharged clients' visits, as the row's figure does.
    queryFn: () =>
      services.scheduled({ workerId, ...week, ...(period ? { includeDischarged: true } : {}) }),
    enabled: has('service:read'),
  });
  const clients = useQuery({
    queryKey: ['directory', 'clients'],
    queryFn: () => directory.clients(),
    enabled: has('service:read') && has('directory:read'),
  });
  const directoryGate = authorityGate(permissions, 'directory:read').kind;
  const clientLabel = useMemo(
    () => clientNameResolver(directoryGate, clients),
    [clients.data, clients.isError, directoryGate],
  );
  const connectors = useConnectorLookup(mayReadIntegrationsHealth(has) && has('service:read'));

  const gate = authorityGate(permissions, 'service:read');
  if (!gateOpen(gate)) {
    return (
      <PanelGate
        gate={gate}
        what="client visits"
        needs="the service permission"
        onRetry={permissions.refetch}
      />
    );
  }
  if (visits.isLoading) return <SkeletonSet surface="table" rows={4} />;
  if (visits.isError && !visits.data) {
    return (
      <LoadFailure
        error={visits.error}
        message="We couldn’t load this person’s visits just now. Do not read this as a week with none."
        onRetry={() => void visits.refetch()}
      />
    );
  }
  const rows = [...(visits.data?.scheduledServices ?? [])].sort((a, b) =>
    a.startsAt.localeCompare(b.startsAt),
  );
  return (
    <section className="rn-card">
      <div className="rn-card-head">
        <h2 className="rn-card-title">
          Client visits · {week.from ? formatDay(week.from) : 'All time'} – {formatDay(week.to)}
        </h2>
        <button
          type="button"
          className="rn-link"
          onClick={() => navigate(`/scheduling?worker=${encodeURIComponent(workerId)}`)}
        >
          On the calendar →
        </button>
      </div>
      {visits.data?.scoped ? <ScopeNote scope={OWN_CASELOAD} /> : null}
      {rows.length === 0 && visits.data?.scoped ? (
        // A narrowed read is not an empty week: this person's visits may be
        // outside what the reader can see.
        <p className="rn-muted">
          Nothing you can see {period ? 'in this period' : 'this week'}. Your view of visits covers{' '}
          {OWN_CASELOAD}, so this person’s visits may fall outside it.
        </p>
      ) : rows.length === 0 ? (
        <SuccessEmpty
          heading={period ? 'No client visits in this period' : 'No client visits this week'}
          message="Visits this person is credited with appear here once they are booked."
        />
      ) : (
        <ul className="tc-rows">
          {rows.map((v) => {
            const { state, detail } = visitState(v);
            return (
              <li key={v.id} className="tc-row">
                <button
                  type="button"
                  className="tc-row-btn"
                  onClick={() => navigate(`/visits?occurrence=${encodeURIComponent(v.id)}`)}
                >
                  <span className="tc-row-main">
                    <span className="tc-row-name">{formatDay(v.startsAt)}</span>
                    <span className="rn-muted">
                      {formatClock(v.startsAt)}
                      {v.endsAt ? ` – ${formatClock(v.endsAt)}` : ''} · {clientLabel(v.clientId)}
                      {detail ? ` · ${detail}` : ''}
                    </span>
                  </span>
                  <span className="tc-row-side">
                    <Pill variant={CALENDAR_STATE_TONE[state]} dot>
                      {calendarStateLabel(
                        state,
                        v.integrationId
                          ? (connectors.get(v.integrationId)?.displayName ?? null)
                          : null,
                      )}
                    </Pill>
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

function WorkerShifts({ workerId }: { workerId: string }) {
  const navigate = useNav();
  const permissions = usePermissions();
  const { has } = permissions;
  const week = useQuery({
    queryKey: ['schedule', 'week', workerId],
    queryFn: () => schedule.workerWeek(workerId),
    enabled: has('schedule:read'),
  });

  const scheduleGate = authorityGate(permissions, 'schedule:read');
  if (!gateOpen(scheduleGate))
    return (
      <PanelGate
        gate={scheduleGate}
        what="the schedule"
        needs="the schedule permission"
        onRetry={permissions.refetch}
      />
    );
  if (week.isLoading) return <SkeletonSet surface="table" rows={5} />;
  const data = week.data;
  // `if (!data) return null` rendered a blank tab panel on failure — no
  // message, no retry, nothing to tell the reader an answer never arrived.
  if (!data) {
    return week.isError ? (
      <LoadFailure
        error={week.error}
        message="We couldn’t load this person’s week just now."
        onRetry={() => void week.refetch()}
      />
    ) : null;
  }

  return (
    <div className="rn-col">
      <section className="rn-card">
        <h2 className="rn-card-title">
          {formatDay(data.weekStart)} – {formatDay(data.weekEnd)}
        </h2>
        <div className="tc-kpis" style={{ marginTop: 'var(--space-12)' }}>
          <div className="db-kpi">
            <span className="db-kpi-label">Shifts</span>
            <span className="db-kpi-value">{data.summary.total}</span>
          </div>
          <div className="db-kpi">
            <span className="db-kpi-label">Covered</span>
            <span className="db-kpi-value">{data.summary.withSession}</span>
            <span className="db-kpi-foot">
              <span className="rn-muted">A clock session fulfilled the shift</span>
            </span>
          </div>
          <div className="db-kpi">
            <span className="db-kpi-label">Uncovered</span>
            <span className="db-kpi-value">{data.summary.withoutSession}</span>
            <span className="db-kpi-foot">
              <span className="rn-muted">No session recorded against it</span>
            </span>
          </div>
        </div>
      </section>

      {/*
        WHEN THEY SAY THEY CAN WORK, beside what they are actually scheduled
        for. Two different quantities — a POTENTIAL and a COMMITMENT — and the
        panel below never sums them, because staff availability is neither an
        obligation on the organization nor a limit on a client.
      */}
      <WorkerAvailability workerId={workerId} />

      <section className="rn-card">
        <h2 className="rn-card-title">Shifts</h2>
        {data.shifts.length === 0 ? (
          <SuccessEmpty
            heading="Nothing scheduled this week"
            message="Shifts appear here once they are planned."
          />
        ) : (
          <ul className="tc-rows">
            {data.shifts.map((s) => (
              <li key={s.id} className="tc-row">
                <button
                  type="button"
                  className="tc-row-btn"
                  disabled={!s.session}
                  onClick={() => s.session && navigate(`/sessions/${s.session.id}`)}
                >
                  <span className="tc-row-main">
                    <span className="tc-row-name">{formatDay(s.startsAt)}</span>
                    <span className="rn-muted">
                      {formatClock(s.startsAt)} – {formatClock(s.endsAt)} · {s.status}
                    </span>
                  </span>
                  <span className="tc-row-side">
                    {s.session ? (
                      <Pill variant="good" dot>
                        Covered
                      </Pill>
                    ) : s.status === 'cancelled' ? (
                      <Pill variant="plain" dot>
                        Cancelled
                      </Pill>
                    ) : (
                      <Pill variant="look" dot>
                        No session
                      </Pill>
                    )}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

// ------------------------------------------------------------------- mileage

function MileageTab({ workerId }: { workerId: string }) {
  const navigate = useNav();
  const permissions = usePermissions();
  const { has } = permissions;
  const list = useQuery({
    queryKey: ['mileage', 'trips', workerId],
    queryFn: () => mileage.trips({ workerId, limit: 100 }),
    enabled: has('mileage:review'),
  });

  const mileageGate = authorityGate(permissions, 'mileage:review');
  if (!gateOpen(mileageGate))
    return (
      <PanelGate
        gate={mileageGate}
        what="mileage"
        needs="the mileage permission"
        onRetry={permissions.refetch}
      />
    );
  if (list.isLoading) return <SkeletonSet surface="table" rows={5} />;

  const trips = list.data?.trips ?? [];
  const totalMiles = trips.reduce((sum, t) => sum + (t.miles ?? 0), 0);
  const mileageUnknown = list.data === undefined;

  return (
    <div className="rn-col">
      <section className="rn-card">
        <div className="rn-card-head">
          <h2 className="rn-card-title">Mileage</h2>
          <button type="button" className="rn-btn-quiet" onClick={() => navigate('/mileage')}>
            Open mileage
          </button>
        </div>
        <div className="tc-kpis" style={{ marginTop: 'var(--space-12)' }}>
          <div className="db-kpi">
            <span className="db-kpi-label">Recorded miles</span>
            {/* "0.0 across 0 trips" is a reimbursement claim. It was being made
                from a failed request. */}
            <span className="db-kpi-value">{mileageUnknown ? '—' : totalMiles.toFixed(1)}</span>
            <span className="db-kpi-foot">
              <span className="rn-muted">
                {mileageUnknown ? 'Not read' : `Across ${trips.length} trips`}
              </span>
            </span>
          </div>
        </div>
        {/* Nested field, read through a guard: a shape surprise here used to
            blank the whole profile rather than drop one line.

            This once reported how many of this person's drives were personal.
            It no longer can, and should not: the reviewer surface receives no
            personal drives at all, so there is no count to report and nothing to
            infer. What remains worth saying is how much of their driving is
            still unanswered, because that is work somebody has to chase. */}
        {(list.data?.awaitingClassification?.count ?? 0) > 0 ? (
          <p className="rn-muted" style={{ marginTop: 'var(--space-12)' }}>
            {list.data?.awaitingClassification?.count ?? 0} drives are waiting on their driver to
            say what they were.
          </p>
        ) : null}
      </section>

      <section className="rn-card">
        <h2 className="rn-card-title">Trips</h2>
        <ListState
          phase={listPhase(list, trips.length)}
          rows={5}
          onRetry={() => void list.refetch()}
          failedMessage="We couldn’t load this person’s drives just now."
          refused={<Refused message="Your account cannot review this person’s mileage." />}
          empty={
            <SuccessEmpty
              heading="No trips recorded"
              message="Work drives appear here once they are detected or entered."
            />
          }
        >
          <ul className="tc-rows">
            {trips.slice(0, 25).map((t) => (
              <li key={t.id} className="tc-row">
                <div className="tc-row-btn" style={{ cursor: 'default' }}>
                  <span className="tc-row-main">
                    <span className="tc-row-name">{formatDay(t.startedAt)}</span>
                    <span className="rn-muted">
                      {formatClock(t.startedAt)} → {formatClock(t.endedAt)}
                    </span>
                  </span>
                  <span className="tc-row-side">
                    <span className="tc-elapsed">{(t.miles ?? 0).toFixed(1)} mi</span>
                  </span>
                </div>
              </li>
            ))}
          </ul>
        </ListState>
      </section>
    </div>
  );
}

// ------------------------------------------------------------------- clients

/**
 * Exported for the component tier. The caseload panel is where the
 * withheld/absent collapse lived (see `clientPicker` below), and mounting it
 * directly tests that panel rather than a page of unrelated queries.
 */
export function ClientsTab({ workerId }: { workerId: string }) {
  const qc = useQueryClient();
  const { has } = usePermissions();
  const canManage = has('directory:manage');
  const assignments = useQuery({
    queryKey: ['directory', 'assignments', workerId],
    queryFn: () => directory.assignments({ workerId, openOnly: true }),
    enabled: has('directory:read'),
  });
  // The assignment edge carries only ids. Initials come from the directory's
  // own default client projection, which is the least-identifying form the
  // domain publishes — this screen never asks for more than that.
  const clients = useQuery({
    queryKey: ['directory', 'clients'],
    queryFn: () => directory.clients(),
    enabled: has('directory:read'),
  });

  // "A client you cannot view" is a statement about permission. It was the
  // fallback for every id the clients query did not contain — including every
  // id, when that query simply failed.
  const initialsFor = useMemo(() => {
    const map = new Map<string, string>();
    for (const c of clients.data ?? []) map.set(c.id, c.initials);
    return (id: string) => {
      const known = map.get(id);
      if (known !== undefined) return known;
      if (clients.isError) return 'Name unavailable';
      if (clients.data === undefined) return '…';
      return 'A client you cannot view';
    };
  }, [clients.data, clients.isError]);

  const assignedClientIds = useMemo(
    () => new Set((assignments.data ?? []).map((a) => a.clientId)),
    [assignments.data],
  );
  const assignableClients = useMemo(
    () => (clients.data ?? []).filter((c) => !assignedClientIds.has(c.id)),
    [clients.data, assignedClientIds],
  );

  /**
   * WHY THERE IS NOBODY TO ASSIGN — a question the assign form did not ask.
   *
   * `GET /directory/clients` charges `can(..., 'directory:read')` at
   * ORGANIZATION scope and 403s rather than returning fewer people, while the
   * gate on the query above is the flattened `has()`, which is true for a
   * grant confined to one program. So a program-scoped supervisor fired the
   * request, got a 403, `clients.data` stayed `undefined`, and the form told
   * them: "There are no clients in the directory yet — add one from Team &
   * caseloads → Clients first."
   *
   * That is a false statement about the organization's records, made to the
   * person least able to check it, and it ends in them creating a duplicate
   * client record. The `initialsFor` resolver eight lines up already tells
   * `Name unavailable` from `…` from `A client you cannot view`; the form
   * simply never asked the same question.
   *
   * `listPhase` over the CLIENTS query, not over `assignableClients.length`:
   * "every client is already assigned" is a real, earned empty and must
   * survive, and it is only knowable once the list actually arrived.
   */
  const clientPicker = pickerState(
    listPhase(clients, assignableClients.length),
    'clients',
    'Select a client…',
  );
  /** The one branch entitled to claim the organization has no clients. */
  const clientListArrived = clients.data !== undefined && !clients.isError;

  const [assigning, setAssigning] = useState(false);
  const [clientId, setClientId] = useState('');
  const [relationship, setRelationship] = useState<'primary' | 'secondary' | 'covering'>('primary');
  const [assignError, setAssignError] = useState<string | null>(null);

  const assign = useMutation({
    mutationFn: () => directory.assignClient({ workerId, clientId, relationship }),
    onSuccess: () => {
      setAssigning(false);
      setClientId('');
      setAssignError(null);
      void qc.invalidateQueries({ queryKey: ['directory', 'assignments', workerId] });
    },
    onError: (err: unknown) => {
      setAssignError(err instanceof Error ? err.message : 'We couldn’t assign that client.');
    },
  });

  const unassign = useMutation({
    mutationFn: (assignmentId: string) => directory.unassign(assignmentId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['directory', 'assignments', workerId] });
    },
  });

  const rows = assignments.data ?? [];

  return (
    <section className="rn-card">
      <div className="rn-card-head">
        <h2 className="rn-card-title">Assigned clients</h2>
        {canManage ? (
          <button
            type="button"
            className="rn-btn-quiet"
            onClick={() => {
              setAssigning((a) => !a);
              setAssignError(null);
            }}
            aria-expanded={assigning}
            disabled={clients.isLoading}
          >
            {assigning ? 'Cancel' : 'Assign a client'}
          </button>
        ) : null}
      </div>
      {/* Initials by default — the directory's own projection. A caseload list
          is not a reason to reveal names. */}
      <p className="rn-muted" style={{ marginTop: 'var(--space-6)' }}>
        Clients are shown by initials. Revealing a full name is a separate, recorded action.
      </p>

      {assigning && canManage ? (
        <form
          className="org-create"
          onSubmit={(e) => {
            e.preventDefault();
            if (clientId) assign.mutate();
          }}
        >
          {assignableClients.length === 0 ? (
            <p className="rn-muted" style={{ fontSize: 13 }} role="status">
              {!clientListArrived
                ? // `note` is null only for `loading`, whose placeholder is the
                  // sentence — "Loading clients…", not a claim about the list.
                  (clientPicker.note ?? clientPicker.placeholder)
                : (clients.data ?? []).length === 0
                  ? 'There are no clients in the directory yet — add one from Team & caseloads → Clients first.'
                  : 'Every client is already assigned to this worker.'}
            </p>
          ) : (
            <div className="org-create-grid">
              <SettingsField label="Client">
                <select
                  style={{ marginBottom: 0 }}
                  value={clientId}
                  onChange={(e) => setClientId(e.target.value)}
                >
                  <option value="">Select a client…</option>
                  {assignableClients.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.initials}
                    </option>
                  ))}
                </select>
              </SettingsField>
              <SettingsField label="Relationship">
                <select
                  style={{ marginBottom: 0 }}
                  value={relationship}
                  onChange={(e) =>
                    setRelationship(e.target.value as 'primary' | 'secondary' | 'covering')
                  }
                >
                  <option value="primary">Primary</option>
                  <option value="secondary">Secondary</option>
                  <option value="covering">Covering</option>
                </select>
              </SettingsField>
            </div>
          )}
          {assignError ? (
            <p style={{ color: 'var(--color-urgent-deep)', fontSize: 13 }} role="alert">
              {assignError}
            </p>
          ) : null}
          {assignableClients.length > 0 ? (
            <div className="set-actions">
              <button
                type="submit"
                className="signin-devbtn"
                style={{ width: 'auto', padding: 'var(--space-10) var(--space-16)' }}
                disabled={!clientId || assign.isPending}
              >
                {assign.isPending ? 'Assigning…' : 'Assign'}
              </button>
              <button type="button" className="rn-btn-quiet" onClick={() => setAssigning(false)}>
                Cancel
              </button>
            </div>
          ) : null}
        </form>
      ) : null}

      <ListState
        phase={listPhase(assignments, rows.length)}
        rows={4}
        onRetry={() => void assignments.refetch()}
        failedMessage="We couldn’t load this person’s caseload just now. Nobody has been unassigned."
        refused={<Refused message="Your account cannot read this person’s caseload." />}
        empty={
          <SuccessEmpty
            heading="No open assignments"
            message={
              canManage
                ? 'Clients assigned to this worker appear here. Assign the first one above.'
                : 'Clients assigned to this worker appear here.'
            }
          />
        }
      >
        <ul className="tc-rows">
          {rows.map((a) => (
            <li key={a.id} className="tc-row">
              <div className="tc-row-btn" style={{ cursor: 'default' }}>
                <span className="tc-row-main">
                  <span className="tc-row-name">{initialsFor(a.clientId)}</span>
                  <span className="rn-muted">
                    {a.relationship} · since {formatDay(a.startedOn)}
                  </span>
                </span>
                {canManage ? (
                  <button
                    type="button"
                    className="rn-link"
                    disabled={unassign.isPending}
                    onClick={() => unassign.mutate(a.id)}
                  >
                    Unassign
                  </button>
                ) : null}
              </div>
            </li>
          ))}
        </ul>
      </ListState>
    </section>
  );
}

/**
 * A tab panel's answer to "may this reader see this part of the profile" — all
 * THREE not-open answers, not just the refusal.
 *
 * It was `Denied`, and it took a `what` and a `permission` and rendered "You
 * can't see X for this worker" unconditionally. Its three callers reached it
 * from a bare `has()`, which is `false` while `/auth/me` is in flight and
 * `false` after it failed, so a reader holding `time:read` met a refusal about
 * their own account on every cold load of the Time tab. The refusal copy is
 * unchanged; what changed is that it is now claimed only from a RESOLVED set,
 * with "we are still checking" and "we could not check" said out loud instead
 * of borrowed from it.
 */
function PanelGate({
  gate,
  what,
  needs,
  onRetry,
}: {
  gate: AuthorityGate;
  what: string;
  needs: string;
  onRetry: () => void;
}) {
  return (
    <GateNotice
      gate={gate}
      what={`see ${what} for this worker`}
      heading={`You can’t see ${what} for this worker`}
      detail={`This needs ${needs}. The rest of the profile is unaffected.`}
      onRetry={onRetry}
    />
  );
}

/**
 * Who this worker reports to, as a sentence — and never a guess.
 *
 * FOUR ANSWERS, and collapsing any pair of them is the defect this app is built
 * against. `readSupervisorId` returns `undefined` when the payload did not
 * carry the field at all, which is NOT "no supervisor": printing "None" there
 * would invite an administrator to repair an org chart that is already right.
 * The remaining three come from `nameResolver`, which already tells a withheld
 * name from a pending one from a failed read.
 */
function supervisorLabel(worker: directory.Worker, nameOf: (id: string) => string): string {
  const id = readSupervisorId(worker);
  if (id === undefined) return 'Not stated in this record';
  if (id === null) return 'Nobody — no supervisor is set';
  return nameOf(id);
}

/**
 * THE WORKER RECORD, EDITABLE.
 *
 * `PATCH /directory/workers/:id` had zero callers in this app. Everything an
 * organization needs to describe itself — who reports to whom, which login
 * belongs to which person, what a contracted week is, when somebody started and
 * when they left — was writable by the API and reachable from nowhere in the
 * product. `directoryLogic.workerUpdatePayload` carries the full account of
 * what that cost; this component is the surface over it.
 *
 * THREE THINGS IT IS CAREFUL ABOUT.
 *
 * 1. ONLY WHAT CHANGED IS SENT. The patch is built from the ORIGINAL record, so
 *    a field nobody opened is omitted rather than sent as `null` — which on
 *    this route means "clear it".
 *
 * 2. A PICKER IS NEVER DRAWN FROM A READ THAT DID NOT ANSWER. A `<select>` over
 *    an empty list is an offer to clear the field, and over a FAILED list it is
 *    that offer made from ignorance. Each of the three pickers below renders
 *    only over an answered read and says so plainly otherwise; the underlying
 *    value is then untouched, which is exactly what the patch builder does with
 *    a field the editor never moved.
 *
 * 3. THE TWO AUTHORITY FIELDS ARE GATED SEPARATELY, because the server gates
 *    them separately and for a reason worth repeating on screen — see
 *    `permissions.mayAssignWorkerAuthority`.
 */
function WorkerEditor({ worker, onClose }: { worker: directory.Worker; onClose: () => void }) {
  const qc = useQueryClient();
  const permissions = usePermissions();
  const { has } = permissions;
  const [draft, setDraft] = useState<WorkerEditDraft | null>(null);
  const [saveError, setSaveError] = useState<{ refused: boolean; message: string } | null>(null);
  const [saved, setSaved] = useState(false);

  const canAssign = mayAssignWorkerAuthority(permissions);
  // Claimed only from a RESOLVED set: `can()` is default-deny, so it is false
  // on every cold load, and telling somebody they lack a permission they hold
  // sends them to ask an admin for it.
  const assignWithheld = !permissions.isLoading && !permissions.isError && !canAssign;

  const canListRoster = mayListWorkers(permissions);
  /**
   * THE PICKER'S THIRD AND FOURTH ANSWERS — the half this file already had for
   * `canAssign` two declarations above and had not written for the roster.
   *
   * The fallback line under the supervisor picker used to be
   * `!canListRoster ? "…needs permission…" : roster.isError ? … : "Loading…"`:
   * the LIST read was four-state and the PERMISSION that gates it was not, so a
   * set that could not be re-read produced "Naming a supervisor needs
   * permission to view people in your organization" — a claim about the
   * account. `mayListWorkers` is `can()` at organization scope and is
   * default-deny, exactly like `canAssign`, so it wants exactly the same
   * treatment.
   *
   * `rosterUnknown` is written as its own name rather than folded into the
   * ternary because "we could not check" and "we checked and no" are different
   * sentences, and the day somebody edits one of them the other has to still be
   * there to edit.
   */
  const rosterWithheld = !permissions.isLoading && !permissions.isError && !canListRoster;
  const rosterUnknown = permissions.isError && !canListRoster;
  const roster = useQuery({
    queryKey: ['directory', 'workers'],
    queryFn: () => directory.workers(),
    enabled: canAssign && canListRoster,
  });
  const people = useQuery({
    queryKey: ['users', 'list'],
    queryFn: () => users.list(),
    // `GET /users` is `user:read`, the same key the roster charges.
    enabled: canAssign && permissions.can('user:read'),
  });
  const programOptions = useQuery({
    queryKey: ['programs', 'options'],
    queryFn: () => programs.list({ status: 'active', limit: 200 }),
    enabled: has('program:read'),
  });

  const working = draft ?? workerEditDraftFrom(worker);
  const patch = workerUpdatePayload(working, worker);
  const problem = workerEditProblem(working);
  const nothingChanged = workerEditIsEmpty(patch);

  const save = useMutation({
    mutationFn: () => directory.updateWorker(worker.id, workerUpdatePayload(working, worker)),
    onMutate: () => {
      setSaveError(null);
      setSaved(false);
    },
    onSuccess: () => {
      setSaved(true);
      setDraft(null);
      void qc.invalidateQueries({ queryKey: ['directory', 'worker', worker.id] });
      void qc.invalidateQueries({ queryKey: ['directory', 'workers'] });
      // A supervisor edge is what supervision compliance is counted over, and a
      // linked login is what the people screen lists, so neither may keep
      // serving the answer from before this save.
      void qc.invalidateQueries({ queryKey: ['supervision'] });
      void qc.invalidateQueries({ queryKey: ['users', 'list'] });
    },
    onError: (err: unknown) => {
      if (isForbidden(err)) {
        // TWO different refusals answer 403 here and they send the reader to
        // two different people, so the message is chosen by what was in the
        // patch rather than by the status code alone.
        setSaveError({
          refused: true,
          message: workerPatchAssignsAuthority(patch)
            ? 'Setting who somebody reports to, or which login belongs to them, needs directory:assign — it decides what other people can see, so it is held apart from ordinary record edits. The rest of your changes were not saved either.'
            : 'Your account cannot change this worker’s record. Editing one needs directory:manage, and reaching this particular person needs permission to view people in your organization or to be their supervisor.',
        });
        return;
      }
      setSaveError({
        refused: false,
        message:
          err instanceof Error && err.message
            ? `We couldn’t save that: ${err.message}. The record is unchanged.`
            : 'We couldn’t save that just now. The record is unchanged.',
      });
    },
  });

  const set = (next: Partial<WorkerEditDraft>) => setDraft({ ...working, ...next });

  return (
    <section className="rn-card" style={{ marginTop: 'var(--space-12)' }}>
      <div className="rn-card-head">
        <h2 className="rn-card-title">Edit this worker’s record</h2>
        <button type="button" className="rn-btn-quiet" onClick={onClose}>
          Close
        </button>
      </div>

      <form
        className="org-create"
        onSubmit={(e) => {
          e.preventDefault();
          if (!problem && !nothingChanged) save.mutate();
        }}
      >
        <p className="rn-muted" style={{ fontSize: 13, marginTop: 0 }}>
          Only the fields you change are sent. Emptying a field that has a value clears it; leaving
          a field alone leaves it alone.
        </p>

        <div className="org-create-grid">
          <SettingsField label="Name">
            <input
              style={{ marginBottom: 0 }}
              value={working.displayName}
              onChange={(e) => set({ displayName: e.target.value })}
            />
          </SettingsField>
          <SettingsField label="Role" hint="Their job title, as your organization says it.">
            <input
              style={{ marginBottom: 0 }}
              value={working.roleTitle}
              onChange={(e) => set({ roleTitle: e.target.value })}
            />
          </SettingsField>

          <SettingsField
            label="Employment status"
            hint="“Inactive” and “terminated” both stop counting somebody as staff; only you know which one is true."
          >
            <select
              style={{ marginBottom: 0 }}
              value={working.employmentStatus}
              onChange={(e) =>
                set({ employmentStatus: e.target.value as WorkerEditDraft['employmentStatus'] })
              }
            >
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="terminated">Terminated</option>
            </select>
          </SettingsField>
          <SettingsField
            label="Expected weekly hours"
            hint="The contracted week. Every “below expected hours” figure in Phibian is measured against this — blank means there is nothing to measure against, not zero."
          >
            <input
              style={{ marginBottom: 0 }}
              inputMode="decimal"
              value={working.expectedWeeklyHours}
              onChange={(e) => set({ expectedWeeklyHours: e.target.value })}
            />
          </SettingsField>

          <SettingsField label="Started">
            <input
              type="date"
              style={{ marginBottom: 0 }}
              value={working.hiredOn}
              onChange={(e) => set({ hiredOn: e.target.value })}
            />
          </SettingsField>
          <SettingsField
            label="Ended"
            hint="The last day they worked here. Recording it does not by itself change their status."
          >
            <input
              type="date"
              style={{ marginBottom: 0 }}
              value={working.endedOn}
              onChange={(e) => set({ endedOn: e.target.value })}
            />
          </SettingsField>
        </div>

        {/* THE PROGRAM PICKER, over a read that narrows SILENTLY. `GET /programs`
            is `allowScoped: true`, so a program-scoped reader gets a shorter
            list and a 200 — and `ProgramListResult.scope` is the server's own
            statement of which of the two happened. It is read here rather than
            reconstructed from the permission set, for the reason `readScope.ts`
            gives: the client is not handed the fields that decision is made
            from. */}
        {has('program:read') && programOptions.data ? (
          <>
            <SettingsField
              label="Primary program"
              hint="Where this person mainly works. It does not restrict what they can be scheduled on."
            >
              <select
                style={{ marginBottom: 0 }}
                value={working.primaryProgramId}
                onChange={(e) => set({ primaryProgramId: e.target.value })}
              >
                <option value="">No primary program</option>
                {programOptions.data.programs.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
                {working.primaryProgramId !== '' &&
                !programOptions.data.programs.some((p) => p.id === working.primaryProgramId) ? (
                  <option value={working.primaryProgramId}>
                    The program on this record (not in the list you can see)
                  </option>
                ) : null}
              </select>
            </SettingsField>
            {programOptions.data.scope !== 'organization' ? (
              <ScopeNote scope="the programs your account reaches" />
            ) : null}
          </>
        ) : (
          <p className="rn-muted" style={{ fontSize: 13 }}>
            {!has('program:read')
              ? 'Choosing a primary program needs the programs permission. This person’s program is unchanged.'
              : programOptions.isError
                ? 'We couldn’t read your programs, so there is no list to choose from. This person’s program is unchanged — it is not being cleared.'
                : 'Loading your programs…'}
          </p>
        )}

        <h3 className="rn-card-title" style={{ fontSize: 15, marginTop: 'var(--space-16)' }}>
          Who this person answers to
        </h3>
        <p className="rn-muted" style={{ fontSize: 13, marginTop: 0 }}>
          These two fields decide what other people can see, so they are held apart from the rest of
          the record. A supervisor reaches the timesheets, sessions and time corrections of the
          people who report to them; a login joined to this record IS this person as far as the
          worker app is concerned.
        </p>

        {assignWithheld ? (
          <Refused
            heading="Not yours to set"
            message="Assigning a supervisor, or joining a login to a worker record, needs directory:assign. Being able to edit the rest of the record deliberately does not include it."
          />
        ) : (
          <div className="org-create-grid">
            {roster.data ? (
              <SettingsField
                label="Reports to"
                hint="The supervision edge the rest of Phibian resolves reach through."
              >
                <select
                  style={{ marginBottom: 0 }}
                  value={working.supervisorId}
                  onChange={(e) => set({ supervisorId: e.target.value })}
                >
                  {/* The fourth state, and it is selectable rather than hidden:
                      while the stored value was never read, "leave it alone" is
                      the only honest option, and choosing anything else is a
                      deliberate act. */}
                  {working.supervisorId === SUPERVISOR_UNREAD ? (
                    <option value={SUPERVISOR_UNREAD}>
                      Not read from this record — leave it as it is
                    </option>
                  ) : null}
                  <option value="">Nobody</option>
                  {roster.data
                    .filter((w) => w.id !== worker.id)
                    .map((w) => (
                      <option key={w.id} value={w.id}>
                        {w.displayName}
                      </option>
                    ))}
                </select>
              </SettingsField>
            ) : (
              <p className="rn-muted" style={{ fontSize: 13 }}>
                {rosterWithheld
                  ? 'Naming a supervisor needs permission to view people in your organization. Their supervisor is unchanged.'
                  : rosterUnknown
                    ? 'We couldn’t check whether you may see the people in your organization, so there is nobody to choose from. This is a connection problem, not a decision about your account — their supervisor is unchanged.'
                    : roster.isError
                      ? 'We couldn’t read your staff list, so there is nobody to choose from. Their supervisor is unchanged — it is not being cleared.'
                      : 'Loading your staff list…'}
              </p>
            )}

            {people.data ? (
              <SettingsField
                label="Sign-in account"
                hint="Only logins not already joined to another worker are listed. Joining the wrong one makes that person this worker everywhere."
              >
                <select
                  style={{ marginBottom: 0 }}
                  value={working.userId}
                  onChange={(e) => set({ userId: e.target.value })}
                >
                  <option value="">No login joined to this record</option>
                  {people.data
                    .filter((p) => p.workerId === null || p.workerId === worker.id)
                    .map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.name} — {p.email}
                      </option>
                    ))}
                </select>
              </SettingsField>
            ) : (
              <p className="rn-muted" style={{ fontSize: 13 }}>
                {!permissions.can('user:read')
                  ? 'Joining a login needs permission to view users in your organization. The login on this record is unchanged.'
                  : people.isError
                    ? 'We couldn’t read the accounts in your organization, so there is nothing to choose from. The login on this record is unchanged — it is not being unjoined.'
                    : 'Loading the accounts in your organization…'}
              </p>
            )}
          </div>
        )}

        {problem ? (
          <p style={{ color: 'var(--color-urgent-deep)', fontSize: 13 }} role="alert">
            {problem}
          </p>
        ) : null}

        {saveError ? (
          saveError.refused ? (
            <Refused heading="Not yours to change" message={saveError.message} />
          ) : (
            <ErrorPanel message={saveError.message} onRetry={() => save.mutate()} />
          )
        ) : null}

        {saved && nothingChanged ? (
          <p className="rn-muted" role="status" style={{ fontSize: 13 }}>
            Saved.
          </p>
        ) : null}

        <div className="set-actions">
          <button
            type="submit"
            className="signin-devbtn"
            style={{ width: 'auto', padding: 'var(--space-10) var(--space-16)' }}
            disabled={Boolean(problem) || nothingChanged || save.isPending}
          >
            {save.isPending ? 'Saving…' : nothingChanged ? 'Nothing changed' : 'Save changes'}
          </button>
          <button type="button" className="rn-btn-quiet" onClick={onClose}>
            Cancel
          </button>
        </div>
      </form>
    </section>
  );
}
